import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  CircularProgress,
  IconButton,
  ThemeProvider,
  createTheme,
  TextField,
  Autocomplete,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
} from "@mui/icons-material";
import { BASE_URL } from "../../../../services/configUrls";
import { format } from "date-fns";

// Import Modal Components
import AddNewFeatureModal from "./AddNewFeatureModal";
import CreateTextStatusModalForMem from "./CreateTextStatusModalForMem";

// Zoho Books-inspired theme
const theme = createTheme({
  typography: {
    fontFamily: '"Poppins", "Lato", "Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 13,
  },
  palette: {
    primary: {
      main: "#2D7FF9",
      light: "#5B9EFA",
      dark: "#1E5FD9",
    },
    success: {
      main: "#0BBE87",
    },
    error: {
      main: "#E85858",
    },
    background: {
      default: "#F9FAFB",
      paper: "#FFFFFF",
    },
    text: {
      primary: "#212529",
      secondary: "#6C757D",
    },
  },
  components: {
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: "13px",
          padding: "12px 16px",
          borderBottom: "1px solid #E5E7EB",
        },
        head: {
          fontWeight: 600,
          color: "#374151",
          backgroundColor: "#F9FAFB",
          borderBottom: "2px solid #E5E7EB",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          borderRadius: "6px",
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: "13px",
          fontWeight: 500,
          padding: "8px 16px",
          borderRadius: "4px",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
        contained: {
          "&:hover": {
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          },
        },
      },
    },
  },
});

const MembershipsTable = () => {
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [featuresModal, setFeaturesModal] = useState({
    open: false,
    features: [],
    membershipName: "",
    membershipId: null,
  });
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    membershipId: null,
    membershipName: "",
  });

  // Edit Membership States
  const [editMembershipModal, setEditMembershipModal] = useState({
    open: false,
    membershipId: null,
    membershipName: "",
    validityYears: 1,
  });
  const [updateMembershipLoading, setUpdateMembershipLoading] = useState(false);

  // States for feature editing
  const [editingFeature, setEditingFeature] = useState(null);
  const [tempStatusValue, setTempStatusValue] = useState("");
  const [updateLoading, setUpdateLoading] = useState(null);
  const [apiStatusOptions, setApiStatusOptions] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Count validation error states
  const [countError, setCountError] = useState(false);
  const [countErrorMessage, setCountErrorMessage] = useState("");

  // State for feature deletion
  const [deleteFeatureModal, setDeleteFeatureModal] = useState({
    open: false,
    featureId: null,
    featureName: "",
    membershipId: null,
  });

  // States for Add Feature functionality
  const [addFeatureModal, setAddFeatureModal] = useState({
    open: false,
    membershipId: null,
  });
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [featureTypes, setFeatureTypes] = useState({});
  const [featureStatusValues, setFeatureStatusValues] = useState({});
  const [featureSearchQuery, setFeatureSearchQuery] = useState("");
  const [featureListSearchQuery, setFeatureListSearchQuery] = useState("");
  const [loadingFeatures, setLoadingFeatures] = useState(false);
  const [submittingFeatures, setSubmittingFeatures] = useState(false);

  // States for Add New Feature Modal
  const [openAddNewFeatureDialog, setOpenAddNewFeatureDialog] = useState(false);
  const [newFeatureNameInput, setNewFeatureNameInput] = useState("");
  const [newFeatureTypeInput, setNewFeatureTypeInput] = useState("text");

  // States for Create Text Status Modal
  const [openCreateTextStatusDialog, setOpenCreateTextStatusDialog] =
    useState(false);
  const [newStatusText, setNewStatusText] = useState("");
  const [newStatusSymbol, setNewStatusSymbol] = useState("");
  const [currentEditingFeature, setCurrentEditingFeature] = useState(null);

  useEffect(() => {
    fetchMemberships();
    fetchStatusOptions();
  }, []);

  const fetchMemberships = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(
        `${BASE_URL}/accounts/memberships-with-features`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      const result = await response.json();

      if (result.data && Array.isArray(result.data)) {
        setMemberships(result.data);

        if (featuresModal.open && featuresModal.membershipId) {
          const updatedMembership = result.data.find(
            (m) => m.membership_id === featuresModal.membershipId
          );
          if (updatedMembership) {
            setFeaturesModal((prev) => ({
              ...prev,
              features: updatedMembership.features || [],
            }));
          }
        }
      }
    } catch (error) {
      console.error("Error fetching memberships:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusOptions = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(`${BASE_URL}/accounts/all-status-options`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const result = await response.json();

      if (Array.isArray(result)) {
        const transformedStatuses = result.map((status) => ({
          id: status.id,
          text: status.status_text,
          symbol: status.symbol,
          color: getColorForSymbol(status.symbol),
        }));
        setApiStatusOptions(transformedStatuses);
      }
    } catch (error) {
      console.error("Error fetching status options:", error);
    }
  };

  const fetchAvailableFeatures = async () => {
    setLoadingFeatures(true);
    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(`${BASE_URL}/accounts/all-feature`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const result = await response.json();

      if (result && Array.isArray(result)) {
        setAvailableFeatures(result);
      }
    } catch (error) {
      console.error("Error fetching features:", error);
    } finally {
      setLoadingFeatures(false);
    }
  };

  const getColorForSymbol = (symbol) => {
    switch (symbol) {
      case "✓":
        return { bg: "#D1FAE5", color: "#065F46" };
      case "✗":
        return { bg: "#F3F4F6", color: "#374151" };
      case "*":
        return { bg: "#DBEAFE", color: "#1E40AF" };
      default:
        return { bg: "#E0E7FF", color: "#3730A3" };
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return format(new Date(dateString), "dd MMM yyyy, hh:mm a");
    } catch (error) {
      return dateString;
    }
  };

  const handleViewFeatures = (membership) => {
    setFeaturesModal({
      open: true,
      features: membership.features || [],
      membershipName: membership.membership_name,
      membershipId: membership.membership_id,
    });
  };

  const handleCloseModal = () => {
    setFeaturesModal((prev) => ({
      ...prev,
      open: false,
    }));
  };

  const handleDeleteClick = (membership) => {
    setDeleteModal({
      open: true,
      membershipId: membership.membership_id,
      membershipName: membership.membership_name,
    });
  };

  const handleCloseDeleteModal = () => {
    if (deleteLoading) return;
    setDeleteModal({
      open: false,
      membershipId: null,
      membershipName: "",
    });
  };

  const handleDeleteMembership = async () => {
    setDeleteLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(
        `${BASE_URL}/accounts/membership/${deleteModal.membershipId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        setMemberships((prevMemberships) =>
          prevMemberships.filter(
            (membership) =>
              membership.membership_id !== deleteModal.membershipId
          )
        );
        handleCloseDeleteModal();
        setSnackbar({
          open: true,
          message: "Membership deleted successfully",
          severity: "success",
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.detail || `Error: ${response.status}`);
      }
    } catch (error) {
      console.error("Error deleting membership:", error);
      setSnackbar({
        open: true,
        message: error.message || "Error deleting membership",
        severity: "error",
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  // Edit Membership Handlers
  const handleEditMembershipClick = (membership) => {
    // Convert validity_days to years if it exists
    const validityYears = membership.validity_days
      ? Math.round(membership.validity_days / 365)
      : 1;

    setEditMembershipModal({
      open: true,
      membershipId: membership.membership_id,
      membershipName: membership.membership_name,
      validityYears: validityYears,
    });
  };

  const handleCloseEditMembershipModal = () => {
    if (updateMembershipLoading) return;
    setEditMembershipModal({
      open: false,
      membershipId: null,
      membershipName: "",
      validityYears: 1,
    });
  };

  const handleUpdateMembership = async () => {
    if (!editMembershipModal.membershipName.trim()) {
      setSnackbar({
        open: true,
        message: "Membership name is required",
        severity: "error",
      });
      return;
    }

    setUpdateMembershipLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");

      // Convert years to days
      const validityDays = editMembershipModal.validityYears * 365;

      const payload = {
        membership_name: editMembershipModal.membershipName.trim(),
        validity_days: validityDays,
      };

      const response = await fetch(
        `${BASE_URL}/accounts/membership/${editMembershipModal.membershipId}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: result.message || "Membership updated successfully",
          severity: "success",
        });

        // Refresh memberships list
        await fetchMemberships();
        handleCloseEditMembershipModal();
      } else {
        throw new Error(result.message || `Error: ${response.status}`);
      }
    } catch (error) {
      console.error("Error updating membership:", error);
      setSnackbar({
        open: true,
        message: error.message || "Error updating membership",
        severity: "error",
      });
    } finally {
      setUpdateMembershipLoading(false);
    }
  };

  // Feature Edit Handlers
  const handleFeatureEdit = (index, feature) => {
    setEditingFeature(index);
    setTempStatusValue(feature.status_value);
    setCountError(false);
    setCountErrorMessage("");
  };

  const handleFeatureEditCancel = () => {
    setEditingFeature(null);
    setTempStatusValue("");
    setCountError(false);
    setCountErrorMessage("");
  };

  const handleFeatureEditSave = async (index) => {
    const filteredFeatures = featuresModal.features.filter((feature) =>
      feature.feature_name
        .toLowerCase()
        .includes(featureListSearchQuery.toLowerCase())
    );
    const feature = filteredFeatures[index];

    if (countError) {
      setSnackbar({
        open: true,
        message: "Please fix the errors before saving",
        severity: "error",
      });
      return;
    }

    const statusValueStr = String(tempStatusValue);

    if (feature.feature_type === "Count") {
      if (isNaN(tempStatusValue) || statusValueStr.trim() === "") {
        setSnackbar({
          open: true,
          message: "Please enter a valid number for count",
          severity: "error",
        });
        return;
      }
    } else {
      if (!statusValueStr.trim()) {
        setSnackbar({
          open: true,
          message: "Please enter a status value",
          severity: "error",
        });
        return;
      }
    }

    setUpdateLoading(index);

    try {
      const accessToken = localStorage.getItem("accessToken");

      let payload;
      if (feature.feature_type === "Count") {
        payload = {
          membership_id: featuresModal.membershipId,
          feature_id: feature.feature_id,
          count_value: parseInt(tempStatusValue),
        };
      } else {
        const selectedStatus = apiStatusOptions.find(
          (status) => status.text === statusValueStr.trim()
        );

        if (!selectedStatus) {
          setSnackbar({
            open: true,
            message: "Please select a valid status from the list",
            severity: "error",
          });
          setUpdateLoading(null);
          return;
        }

        payload = {
          membership_id: featuresModal.membershipId,
          feature_id: feature.feature_id,
          status_option_id: selectedStatus.id,
        };
      }

      const response = await fetch(
        `${BASE_URL}/accounts/membership-feature-update`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: result.message || "Feature updated successfully",
          severity: "success",
        });

        setEditingFeature(null);
        setTempStatusValue("");
        setCountError(false);
        setCountErrorMessage("");

        await fetchMemberships();
      } else {
        setSnackbar({
          open: true,
          message: result.message || "Failed to update feature",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error updating feature:", error);
      setSnackbar({
        open: true,
        message: "Error updating feature. Please try again.",
        severity: "error",
      });
    } finally {
      setUpdateLoading(null);
    }
  };

  // Feature Delete Handlers
  const handleDeleteFeatureClick = (feature) => {
    setDeleteFeatureModal({
      open: true,
      featureId: feature.feature_id,
      featureName: feature.feature_name,
      membershipId: featuresModal.membershipId,
    });
  };

  const handleCloseDeleteFeatureModal = () => {
    setDeleteFeatureModal({
      open: false,
      featureId: null,
      featureName: "",
      membershipId: null,
    });
  };

  const handleDeleteFeature = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");

      const response = await fetch(
        `${BASE_URL}/accounts/membership-feature?membership_id=${deleteFeatureModal.membershipId}&feature_id=${deleteFeatureModal.featureId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: result.message || "Feature removed successfully",
          severity: "success",
        });

        await fetchMemberships();
      } else {
        setSnackbar({
          open: true,
          message: result.message || "Failed to remove feature",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error removing feature:", error);
      setSnackbar({
        open: true,
        message: "Error removing feature. Please try again.",
        severity: "error",
      });
    } finally {
      handleCloseDeleteFeatureModal();
    }
  };

  // Add Feature Handlers
  const handleOpenAddFeatureModal = (membershipId) => {
    setAddFeatureModal({
      open: true,
      membershipId: membershipId,
    });
    fetchAvailableFeatures();
  };

  const handleCloseAddFeatureModal = () => {
    if (!submittingFeatures) {
      setAddFeatureModal({
        open: false,
        membershipId: null,
      });
      setSelectedFeatures([]);
      setFeatureTypes({});
      setFeatureStatusValues({});
      setFeatureSearchQuery("");
    }
  };

  const handleFeatureToggle = (featureName) => {
    setSelectedFeatures((prev) => {
      const newSelection = prev.includes(featureName)
        ? prev.filter((name) => name !== featureName)
        : [...prev, featureName];

      if (!newSelection.includes(featureName)) {
        setFeatureTypes((prevTypes) => {
          const newTypes = { ...prevTypes };
          delete newTypes[featureName];
          return newTypes;
        });
        setFeatureStatusValues((prevValues) => {
          const newValues = { ...prevValues };
          delete newValues[featureName];
          return newValues;
        });
      } else if (!featureTypes[featureName]) {
        const feature = availableFeatures.find(
          (f) => f.feature_name === featureName
        );
        setFeatureTypes((prevTypes) => ({
          ...prevTypes,
          [featureName]: feature?.type.toLowerCase() || "text",
        }));
        setFeatureStatusValues((prevValues) => ({
          ...prevValues,
          [featureName]: feature?.type === "Count" ? "0" : "TBA",
        }));
      }

      return newSelection;
    });
  };

  const handleFeatureStatusValueChange = (featureName, value) => {
    setFeatureStatusValues((prev) => ({
      ...prev,
      [featureName]: value,
    }));
  };

  const handleAddFeatureBulk = async () => {
    if (selectedFeatures.length === 0 || !addFeatureModal.membershipId) {
      return;
    }

    setSubmittingFeatures(true);
    try {
      const accessToken = localStorage.getItem("accessToken");

      const featureStatusMappings = selectedFeatures.map((featureName) => {
        const feature = availableFeatures.find(
          (f) => f.feature_name === featureName
        );

        if (feature.type === "Count") {
          return {
            feature_id: feature.feature_id,
            count_value: parseInt(featureStatusValues[featureName] || "0"),
          };
        } else {
          const statusValue = featureStatusValues[featureName] || "TBA";
          const statusOption = apiStatusOptions.find(
            (opt) => opt.text === statusValue
          );

          return {
            feature_id: feature.feature_id,
            status_option_id: statusOption?.id || null,
          };
        }
      });

      const payload = {
        membership_id: addFeatureModal.membershipId,
        feature_status_mappings: featureStatusMappings,
      };

      const response = await fetch(
        `${BASE_URL}/accounts/membership-feature-status-bulk`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setSnackbar({
          open: true,
          message: result.message || "Features added successfully",
          severity: "success",
        });

        await fetchMemberships();

        handleCloseAddFeatureModal();
      } else {
        setSnackbar({
          open: true,
          message: result.message || "Failed to add features",
          severity: "error",
        });
      }
    } catch (error) {
      console.error("Error adding features:", error);
      setSnackbar({
        open: true,
        message: "Error adding features. Please try again.",
        severity: "error",
      });
    } finally {
      setSubmittingFeatures(false);
    }
  };

  const handleRefreshAfterFeatureCreated = async () => {
    await fetchAvailableFeatures();
    await fetchStatusOptions();
  };

  const getFilteredAndSortedFeatures = () => {
    const currentFeatureIds = featuresModal.features.map((f) => f.feature_id);

    const filtered = availableFeatures.filter(
      (feature) =>
        !currentFeatureIds.includes(feature.feature_id) &&
        feature.feature_name
          .toLowerCase()
          .includes(featureSearchQuery.toLowerCase())
    );

    const selected = [];
    const unselected = [];

    filtered.forEach((feature) => {
      if (selectedFeatures.includes(feature.feature_name)) {
        selected.push(feature);
      } else {
        unselected.push(feature);
      }
    });

    return [...selected, ...unselected];
  };

  const getStatusColor = (type) => {
    if (type?.toLowerCase() === "count") {
      return { bg: "#E0E7FF", color: "#3730A3" };
    }
    return { bg: "#D1FAE5", color: "#065F46" };
  };

  const getStatusDisplay = (statusValue, type, symbol) => {
    if (type?.toLowerCase() === "count") {
      return statusValue;
    }
    if (symbol && symbol !== "null") {
      return `${symbol} ${statusValue}`;
    }
    return statusValue;
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ width: "100%" }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: "5%" }}>#</TableCell>
                  <TableCell sx={{ width: "25%" }}>Membership Name</TableCell>
                  <TableCell sx={{ width: "20%" }}>Created At</TableCell>
                  <TableCell sx={{ width: "20%" }}>Updated At</TableCell>
                  <TableCell sx={{ width: "30%", textAlign: "center" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {memberships.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ color: "#6C757D", fontSize: "13px" }}>
                        No memberships found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  memberships.map((membership, index) => (
                    <TableRow
                      key={membership.membership_id}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#F9FAFB",
                        },
                      }}
                    >
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#6C757D",
                            fontWeight: 600,
                          }}
                        >
                          {index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#212529",
                            fontWeight: 500,
                          }}
                        >
                          {membership.membership_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: "13px", color: "#6C757D" }}>
                          {formatDateTime(membership.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: "13px", color: "#6C757D" }}>
                          {formatDateTime(membership.updated_at)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                            onClick={() =>
                              handleEditMembershipClick(membership)
                            }
                            sx={{
                              fontSize: "12px",
                              padding: "4px 12px",
                            }}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                            onClick={() => handleViewFeatures(membership)}
                            sx={{
                              fontSize: "12px",
                              padding: "4px 12px",
                            }}
                          >
                            View ({membership.features?.length || 0})
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
                            onClick={() => handleDeleteClick(membership)}
                            sx={{
                              fontSize: "12px",
                              padding: "4px 12px",
                            }}
                          >
                            Delete
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Features Modal with Edit, Delete & Add */}
        <Dialog
          open={featuresModal.open}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              animation: featuresModal.open ? "slideUp 0.3s ease-out" : "none",
              "@keyframes slideUp": {
                from: {
                  opacity: 0,
                  transform: "translateY(20px)",
                },
                to: {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              },
            },
          }}
        >
          <DialogTitle
            sx={{
              fontSize: "15px",
              fontWeight: 600,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              pb: 1,
            }}
          >
            <Box>
              Features - {featuresModal.membershipName}
              <Typography
                variant="caption"
                sx={{ display: "block", color: "#6C757D", fontWeight: 400 }}
              >
                {featuresModal.features.length} feature(s) available
              </Typography>
            </Box>
            <IconButton size="small" onClick={handleCloseModal}>
              <CloseIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {featuresModal.features.length === 0 ? (
              <Box sx={{ py: 4, textAlign: "center" }}>
                <Typography sx={{ color: "#6C757D", fontSize: "13px", mb: 2 }}>
                  No features added to this membership
                </Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() =>
                    handleOpenAddFeatureModal(featuresModal.membershipId)
                  }
                  variant="contained"
                  size="small"
                >
                  Add Your First Feature
                </Button>
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    display: "flex",
                    gap: 2,
                    mb: 2,
                    alignItems: "center",
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search features..."
                    value={featureListSearchQuery}
                    onChange={(e) => setFeatureListSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <SearchIcon
                          sx={{ mr: 1, color: "#9CA3AF", fontSize: 20 }}
                        />
                      ),
                    }}
                    sx={{
                      flex: 1,
                      "& .MuiOutlinedInput-root": {
                        fontSize: "13px",
                      },
                    }}
                  />
                  <Button
                    startIcon={<AddIcon />}
                    onClick={() =>
                      handleOpenAddFeatureModal(featuresModal.membershipId)
                    }
                    size="small"
                    variant="outlined"
                    sx={{
                      whiteSpace: "nowrap",
                      minWidth: "fit-content",
                    }}
                  >
                    Add More
                  </Button>
                </Box>

                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: "10%" }}>#</TableCell>
                        <TableCell sx={{ width: "35%" }}>
                          Feature Name
                        </TableCell>
                        <TableCell sx={{ width: "15%" }}>Type</TableCell>
                        <TableCell sx={{ width: "30%" }}>Status</TableCell>
                        <TableCell sx={{ width: "10%", textAlign: "center" }}>
                          Action
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {featuresModal.features
                        .filter((feature) =>
                          feature.feature_name
                            .toLowerCase()
                            .includes(featureListSearchQuery.toLowerCase())
                        )
                        .map((feature, index) => (
                          <TableRow key={feature.membership_feature_id}>
                            <TableCell>
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  color: "#6C757D",
                                  fontWeight: 600,
                                }}
                              >
                                {index + 1}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                sx={{
                                  fontSize: "13px",
                                  color: "#212529",
                                  fontWeight: 500,
                                }}
                              >
                                {feature.feature_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={feature.feature_type}
                                size="small"
                                sx={{
                                  backgroundColor:
                                    feature.feature_type?.toLowerCase() ===
                                    "count"
                                      ? "#E0E7FF"
                                      : "#FEF3C7",
                                  color:
                                    feature.feature_type?.toLowerCase() ===
                                    "count"
                                      ? "#3730A3"
                                      : "#92400E",
                                  fontWeight: 600,
                                  fontSize: "11px",
                                  height: "22px",
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {editingFeature === index ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    gap: 1,
                                    alignItems: "flex-start",
                                  }}
                                >
                                  {feature.feature_type === "Count" ? (
                                    <TextField
                                      autoFocus
                                      size="small"
                                      type="number"
                                      value={tempStatusValue}
                                      error={countError}
                                      helperText={
                                        countError ? countErrorMessage : ""
                                      }
                                      onChange={(e) => {
                                        const newValue = e.target.value;

                                        setTempStatusValue(newValue);

                                        const numValue = parseInt(newValue);
                                        if (
                                          newValue === "" ||
                                          isNaN(numValue)
                                        ) {
                                          setCountError(true);
                                          setCountErrorMessage(
                                            "Please enter a valid count"
                                          );
                                        } else if (numValue < 1) {
                                          setCountError(true);
                                          setCountErrorMessage(
                                            "Count must be at least 1"
                                          );
                                        } else {
                                          setCountError(false);
                                          setCountErrorMessage("");
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        if (
                                          e.key === "-" ||
                                          e.key === "e" ||
                                          e.key === "E" ||
                                          e.key === "+"
                                        ) {
                                          e.preventDefault();
                                        }
                                      }}
                                      placeholder="Enter count"
                                      inputProps={{
                                        min: 1,
                                      }}
                                      sx={{
                                        width: 120,
                                        "& .MuiOutlinedInput-root": {
                                          fontSize: "13px",
                                        },
                                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                                          {
                                            WebkitAppearance: "none",
                                            margin: 0,
                                          },
                                        "& input[type=number]": {
                                          MozAppearance: "textfield",
                                        },
                                      }}
                                    />
                                  ) : (
                                    <Autocomplete
                                      freeSolo
                                      autoFocus
                                      options={apiStatusOptions
                                        .sort((a, b) => b.id - a.id)
                                        .map((st) => st.text)}
                                      value={tempStatusValue}
                                      onChange={(event, newValue) => {
                                        if (newValue) {
                                          setTempStatusValue(newValue);
                                        }
                                      }}
                                      onInputChange={(
                                        event,
                                        newInputValue,
                                        reason
                                      ) => {
                                        if (reason === "input") {
                                          setTempStatusValue(newInputValue);
                                        }
                                      }}
                                      ListboxProps={{
                                        style: {
                                          maxHeight: "250px",
                                          padding: 0,
                                        },
                                      }}
                                      ListboxComponent={React.forwardRef(
                                        (props, ref) => {
                                          const { children, ...other } = props;
                                          return (
                                            <ul
                                              {...other}
                                              ref={ref}
                                              style={{ padding: 0, margin: 0 }}
                                            >
                                              {/* Sticky "Create New" option */}
                                              <Box
                                                component="li"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setCurrentEditingFeature(
                                                    index
                                                  );
                                                  setOpenCreateTextStatusDialog(
                                                    true
                                                  );
                                                }}
                                                sx={{
                                                  position: "sticky",
                                                  top: 0,
                                                  zIndex: 10,
                                                  backgroundColor: "#FFFFFF",
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 1,
                                                  padding: "8px 14px",
                                                  cursor: "pointer",
                                                  borderBottom:
                                                    "1px solid #E5E7EB",
                                                  listStyle: "none",
                                                  "&:hover": {
                                                    backgroundColor: "#F0F9FF",
                                                  },
                                                }}
                                              >
                                                <AddIcon
                                                  sx={{
                                                    fontSize: 16,
                                                    color: "#2D7FF9",
                                                  }}
                                                />
                                                <Typography
                                                  sx={{
                                                    fontSize: "13px",
                                                    color: "#2D7FF9",
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  Create New...
                                                </Typography>
                                              </Box>
                                              {/* Scrollable status list */}
                                              {children}
                                            </ul>
                                          );
                                        }
                                      )}
                                      renderOption={(props, option) => (
                                        <li {...props} key={option}>
                                          <Box
                                            sx={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                            }}
                                          >
                                            {apiStatusOptions.find(
                                              (st) => st.text === option
                                            )?.symbol && (
                                              <Chip
                                                label={
                                                  apiStatusOptions.find(
                                                    (st) => st.text === option
                                                  )?.symbol || ""
                                                }
                                                size="small"
                                                sx={{
                                                  backgroundColor:
                                                    getColorForSymbol(
                                                      apiStatusOptions.find(
                                                        (st) =>
                                                          st.text === option
                                                      )?.symbol
                                                    ).bg,
                                                  color: getColorForSymbol(
                                                    apiStatusOptions.find(
                                                      (st) => st.text === option
                                                    )?.symbol
                                                  ).color,
                                                  fontSize: "11px",
                                                  fontWeight: 600,
                                                  height: "20px",
                                                }}
                                              />
                                            )}
                                            <Typography
                                              sx={{ fontSize: "13px" }}
                                            >
                                              {option}
                                            </Typography>
                                          </Box>
                                        </li>
                                      )}
                                      renderInput={(params) => (
                                        <TextField
                                          {...params}
                                          size="small"
                                          placeholder="Select status"
                                          sx={{
                                            "& .MuiOutlinedInput-root": {
                                              fontSize: "13px",
                                            },
                                          }}
                                        />
                                      )}
                                      sx={{ flex: 1, minWidth: 180 }}
                                    />
                                  )}

                                  <Box sx={{ display: "flex", gap: 0.5 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() =>
                                        handleFeatureEditSave(index)
                                      }
                                      disabled={updateLoading === index}
                                      sx={{
                                        color: "#0BBE87",
                                        "&:hover": {
                                          backgroundColor: "#D1FAE5",
                                        },
                                      }}
                                    >
                                      {updateLoading === index ? (
                                        <CircularProgress
                                          size={16}
                                          sx={{ color: "#0BBE87" }}
                                        />
                                      ) : (
                                        <CheckIcon sx={{ fontSize: 18 }} />
                                      )}
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      onClick={handleFeatureEditCancel}
                                      disabled={updateLoading === index}
                                      sx={{
                                        color: "#E85858",
                                        "&:hover": {
                                          backgroundColor: "#FEE2E2",
                                        },
                                      }}
                                    >
                                      <CloseIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                  </Box>
                                </Box>
                              ) : (
                                <Box
                                  onClick={() =>
                                    handleFeatureEdit(index, feature)
                                  }
                                  sx={{
                                    cursor: "pointer",
                                    display: "inline-block",
                                    "&:hover": {
                                      backgroundColor: "#F9FAFB",
                                    },
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: "4px",
                                  }}
                                >
                                  <Chip
                                    label={getStatusDisplay(
                                      feature.status_value,
                                      feature.feature_type,
                                      feature.symbol
                                    )}
                                    size="small"
                                    sx={{
                                      backgroundColor: getColorForSymbol(
                                        feature.symbol
                                      ).bg,
                                      color: getColorForSymbol(feature.symbol)
                                        .color,
                                      fontWeight: 600,
                                      fontSize: "11px",
                                      cursor: "pointer",
                                    }}
                                  />
                                </Box>
                              )}
                            </TableCell>
                            <TableCell sx={{ textAlign: "center" }}>
                              {editingFeature !== index && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() =>
                                    handleDeleteFeatureClick(feature)
                                  }
                                  sx={{ fontSize: "16px" }}
                                >
                                  <DeleteIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Membership Confirmation Modal */}
        <Dialog
          open={deleteModal.open}
          onClose={handleCloseDeleteModal}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontSize: "15px", fontWeight: 600 }}>
            Confirm Delete
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: "13px", color: "#6C757D" }}>
              Are you sure you want to delete the membership "
              <strong>{deleteModal.membershipName}</strong>"? This action cannot
              be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleCloseDeleteModal}
              disabled={deleteLoading}
              sx={{ fontSize: "13px" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteMembership}
              variant="contained"
              color="error"
              disabled={deleteLoading}
              startIcon={
                deleteLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <DeleteIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{ fontSize: "13px" }}
            >
              {deleteLoading ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Membership Modal */}
        <Dialog
          open={editMembershipModal.open}
          onClose={handleCloseEditMembershipModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ fontSize: "15px", fontWeight: 600 }}>
            Edit Membership
          </DialogTitle>
          <DialogContent>
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 2 }}
            >
              <TextField
                fullWidth
                label="Membership Name"
                value={editMembershipModal.membershipName}
                onChange={(e) =>
                  setEditMembershipModal((prev) => ({
                    ...prev,
                    membershipName: e.target.value,
                  }))
                }
                disabled={updateMembershipLoading}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: "13px",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "13px",
                  },
                }}
              />
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: "13px" }} id="validity-years-label">
                  Validity Years
                </InputLabel>
                <Select
                  labelId="validity-years-label"
                  value={editMembershipModal.validityYears}
                  label="Validity Years"
                  onChange={(e) =>
                    setEditMembershipModal((prev) => ({
                      ...prev,
                      validityYears: e.target.value,
                    }))
                  }
                  disabled={updateMembershipLoading}
                  sx={{
                    fontSize: "13px",
                  }}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((year) => (
                    <MenuItem key={year} value={year} sx={{ fontSize: "13px" }}>
                      {year} {year === 1 ? "Year" : "Years"} ({year * 365} days)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleCloseEditMembershipModal}
              disabled={updateMembershipLoading}
              sx={{ fontSize: "13px" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateMembership}
              variant="contained"
              disabled={updateMembershipLoading}
              startIcon={
                updateMembershipLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CheckIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{ fontSize: "13px" }}
            >
              {updateMembershipLoading ? "Updating..." : "Update"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Feature Confirmation Modal */}
        <Dialog
          open={deleteFeatureModal.open}
          onClose={handleCloseDeleteFeatureModal}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontSize: "15px", fontWeight: 600 }}>
            Remove Feature
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: "13px", color: "#6C757D" }}>
              Are you sure you want to remove the feature "
              <strong>{deleteFeatureModal.featureName}</strong>" from this
              membership?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={handleCloseDeleteFeatureModal}
              sx={{ fontSize: "13px" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteFeature}
              variant="contained"
              color="error"
              startIcon={<DeleteIcon sx={{ fontSize: 16 }} />}
              sx={{ fontSize: "13px" }}
            >
              Remove
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add Feature Modal */}
        <Dialog
          open={addFeatureModal.open}
          onClose={submittingFeatures ? undefined : handleCloseAddFeatureModal}
          maxWidth="sm"
          fullWidth
          disableEscapeKeyDown={submittingFeatures}
        >
          <DialogTitle sx={{ fontSize: "15px", fontWeight: 600, pb: 1 }}>
            Select Features
          </DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              size="small"
              placeholder="Search features..."
              value={featureSearchQuery}
              onChange={(e) => setFeatureSearchQuery(e.target.value)}
              disabled={submittingFeatures}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: "#9CA3AF", fontSize: 20 }} />
                ),
              }}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  fontSize: "13px",
                },
              }}
            />

            <Typography
              variant="caption"
              sx={{
                color: "#6C757D",
                fontSize: "12px",
                mb: 2,
                display: "block",
              }}
            >
              {selectedFeatures.length > 0
                ? `${selectedFeatures.length} selected`
                : "Select features to add"}
            </Typography>

            {loadingFeatures ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 4,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            ) : (
              <Box sx={{ maxHeight: 400, overflowY: "auto", pr: 0.5 }}>
                <Button
                  fullWidth
                  startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                  onClick={() => setOpenAddNewFeatureDialog(true)}
                  variant="outlined"
                  disabled={submittingFeatures}
                  sx={{
                    mb: 2,
                    justifyContent: "flex-start",
                    fontSize: "13px",
                    py: 1,
                    borderColor: "#2D7FF9",
                    color: "#2D7FF9",
                    "&:hover": {
                      backgroundColor: "#F0F7FF",
                      borderColor: "#2D7FF9",
                    },
                  }}
                >
                  Add New Feature
                </Button>

                {getFilteredAndSortedFeatures().map((feature) => (
                  <Box key={feature.feature_id} sx={{ mb: 1.5 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedFeatures.includes(
                            feature.feature_name
                          )}
                          onChange={() =>
                            handleFeatureToggle(feature.feature_name)
                          }
                          disabled={submittingFeatures}
                          size="small"
                          sx={{
                            color: "#9CA3AF",
                            p: 0.5,
                            "&.Mui-checked": {
                              color: "#2D7FF9",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                          {feature.feature_name}
                        </Typography>
                      }
                      sx={{
                        mx: 0,
                        mb: selectedFeatures.includes(feature.feature_name)
                          ? 1
                          : 0,
                        width: "100%",
                      }}
                    />

                    {selectedFeatures.includes(feature.feature_name) && (
                      <Box sx={{ ml: 4, mb: 1 }}>
                        {feature.type === "Count" ? (
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            label="Count Value"
                            value={
                              featureStatusValues[feature.feature_name] || "0"
                            }
                            onChange={(e) =>
                              handleFeatureStatusValueChange(
                                feature.feature_name,
                                e.target.value
                              )
                            }
                            disabled={submittingFeatures}
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                fontSize: "13px",
                              },
                              "& .MuiInputLabel-root": {
                                fontSize: "13px",
                              },
                            }}
                          />
                        ) : (
                          <Autocomplete
                            freeSolo
                            disabled={submittingFeatures}
                            options={apiStatusOptions
                              .sort((a, b) => b.id - a.id)
                              .map((st) => st.text)}
                            value={
                              featureStatusValues[feature.feature_name] || "TBA"
                            }
                            onChange={(event, newValue) => {
                              if (newValue) {
                                handleFeatureStatusValueChange(
                                  feature.feature_name,
                                  newValue
                                );
                              }
                            }}
                            onInputChange={(event, newInputValue) => {
                              handleFeatureStatusValueChange(
                                feature.feature_name,
                                newInputValue
                              );
                            }}
                            ListboxProps={{
                              style: { maxHeight: "250px", padding: 0 },
                            }}
                            ListboxComponent={React.forwardRef((props, ref) => {
                              const { children, ...other } = props;
                              return (
                                <ul
                                  {...other}
                                  ref={ref}
                                  style={{ padding: 0, margin: 0 }}
                                >
                                  {/* Sticky "Create New" option */}
                                  <Box
                                    component="li"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setCurrentEditingFeature(
                                        feature.feature_name
                                      );
                                      setOpenCreateTextStatusDialog(true);
                                    }}
                                    sx={{
                                      position: "sticky",
                                      top: 0,
                                      zIndex: 10,
                                      backgroundColor: "#FFFFFF",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      padding: "8px 14px",
                                      cursor: "pointer",
                                      borderBottom: "1px solid #E5E7EB",
                                      listStyle: "none",
                                      "&:hover": {
                                        backgroundColor: "#F0F9FF",
                                      },
                                    }}
                                  >
                                    <AddIcon
                                      sx={{
                                        fontSize: 16,
                                        color: "#2D7FF9",
                                      }}
                                    />
                                    <Typography
                                      sx={{
                                        fontSize: "13px",
                                        color: "#2D7FF9",
                                        fontWeight: 600,
                                      }}
                                    >
                                      Create New...
                                    </Typography>
                                  </Box>
                                  {/* Scrollable status list */}
                                  {children}
                                </ul>
                              );
                            })}
                            renderOption={(props, option) => (
                              <li {...props} key={option}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  {apiStatusOptions.find(
                                    (st) => st.text === option
                                  )?.symbol && (
                                    <Chip
                                      label={
                                        apiStatusOptions.find(
                                          (st) => st.text === option
                                        )?.symbol || ""
                                      }
                                      size="small"
                                      sx={{
                                        backgroundColor: getColorForSymbol(
                                          apiStatusOptions.find(
                                            (st) => st.text === option
                                          )?.symbol
                                        ).bg,
                                        color: getColorForSymbol(
                                          apiStatusOptions.find(
                                            (st) => st.text === option
                                          )?.symbol
                                        ).color,
                                        fontSize: "11px",
                                        fontWeight: 600,
                                        height: "20px",
                                      }}
                                    />
                                  )}
                                  <Typography sx={{ fontSize: "13px" }}>
                                    {option}
                                  </Typography>
                                </Box>
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                label="Status Value"
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    fontSize: "13px",
                                  },
                                  "& .MuiInputLabel-root": {
                                    fontSize: "13px",
                                  },
                                }}
                              />
                            )}
                          />
                        )}
                      </Box>
                    )}
                  </Box>
                ))}

                {!loadingFeatures &&
                  getFilteredAndSortedFeatures().length === 0 &&
                  !featureSearchQuery && (
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 4,
                        px: 2,
                        backgroundColor: "#F9FAFB",
                        borderRadius: "4px",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#6C757D",
                          fontSize: "13px",
                        }}
                      >
                        All features have been added
                      </Typography>
                    </Box>
                  )}

                {!loadingFeatures &&
                  getFilteredAndSortedFeatures().length === 0 &&
                  featureSearchQuery && (
                    <Box
                      sx={{
                        textAlign: "center",
                        py: 4,
                        px: 2,
                        backgroundColor: "#F9FAFB",
                        borderRadius: "4px",
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#6C757D",
                          fontSize: "13px",
                        }}
                      >
                        No features found matching "{featureSearchQuery}"
                      </Typography>
                    </Box>
                  )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button
              onClick={handleCloseAddFeatureModal}
              disabled={submittingFeatures}
              sx={{ fontSize: "13px" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAddFeatureBulk}
              variant="contained"
              disabled={selectedFeatures.length === 0 || submittingFeatures}
              startIcon={
                submittingFeatures ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <AddIcon sx={{ fontSize: 16 }} />
                )
              }
              sx={{ fontSize: "13px" }}
            >
              {submittingFeatures
                ? "Adding..."
                : `Add${
                    selectedFeatures.length > 0
                      ? ` (${selectedFeatures.length})`
                      : ""
                  }`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Add New Feature Modal */}
        <AddNewFeatureModal
          open={openAddNewFeatureDialog}
          onClose={() => setOpenAddNewFeatureDialog(false)}
          newFeatureNameInput={newFeatureNameInput}
          setNewFeatureNameInput={setNewFeatureNameInput}
          newFeatureTypeInput={newFeatureTypeInput}
          setNewFeatureTypeInput={setNewFeatureTypeInput}
          onFeatureCreated={handleRefreshAfterFeatureCreated}
        />

        {/* Create Text Status Modal - Compact Version */}
        <CreateTextStatusModalForMem
          open={openCreateTextStatusDialog}
          onClose={() => {
            setOpenCreateTextStatusDialog(false);
            setCurrentEditingFeature(null);
          }}
          newStatusText={newStatusText}
          setNewStatusText={setNewStatusText}
          newStatusSymbol={newStatusSymbol}
          setNewStatusSymbol={setNewStatusSymbol}
          onRefreshStatusOptions={fetchStatusOptions}
          onShowSnackbar={setSnackbar}
          onResetTempValue={(newValue) => {
            if (newValue && currentEditingFeature !== null) {
              // Check if we're in edit mode (number index) or add mode (string feature name)
              if (typeof currentEditingFeature === "number") {
                // Edit mode - update tempStatusValue
                setTempStatusValue(newValue);
              } else {
                // Add mode - update featureStatusValues
                handleFeatureStatusValueChange(currentEditingFeature, newValue);
              }
            }
            setCurrentEditingFeature(null);
          }}
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%", fontSize: "13px" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </ThemeProvider>
  );
};

export default MembershipsTable;
